import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';

interface Document {
  id: string;
  title: string;
  status: string;
  file_size: number;
  created_at: string;
  updated_at: string;
  user_id: string;
}

interface DocumentListProps {
  userId?: string;
  status?: string;
  refreshTrigger: number;
}

export default function DocumentList({ userId, status, refreshTrigger }: DocumentListProps) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDocuments();
  }, [userId, status, refreshTrigger]);

  const loadDocuments = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (userId) params.append('userId', userId);
      if (status) params.append('status', status);

      const response = await fetch(`${API_URL}/api/documents?${params}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao carregar documentos');
      }

      setDocuments(data.data.documents || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      'uploaded': '⏳ Enviado',
      'pending_verification': '👀 Aguardando',
      'verified': '✅ Verificado',
      'rejected': '❌ Rejeitado'
    };
    return badges[status] || status;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) return <div className="loading">Carregando...</div>;
  if (error) return <div className="alert alert-error">{error}</div>;
  if (!documents.length) return <div className="empty-state">Nenhum documento encontrado</div>;

  return (
    <div className="document-list">
      <table className="documents-table">
        <thead>
          <tr>
            <th>Título</th>
            <th>Status</th>
            <th>Tamanho</th>
            <th>Data de Envio</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {documents.map((doc) => (
            <tr key={doc.id}>
              <td className="title">{doc.title}</td>
              <td>
                <span className={`status-badge status-${doc.status}`}>
                  {getStatusBadge(doc.status)}
                </span>
              </td>
              <td>{formatFileSize(doc.file_size)}</td>
              <td>{new Date(doc.created_at).toLocaleDateString('pt-BR')}</td>
              <td>
                <button className="btn btn-small">Ver</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
